import type { BrowserRuntime, SupportSessionStatus, StreamMode, UserRole } from '@br/protocol';

export const STATUS_LABEL: Record<SupportSessionStatus, string> = {
  waiting_customer: '고객 대기',
  customer_working: '고객 작업 중',
  customer_done: '고객 완료',
  operator_working: '운영자 작업 중',
  completed: '완료',
};

export const STATUS_TONE: Record<
  SupportSessionStatus,
  'neutral' | 'info' | 'success' | 'warning' | 'muted'
> = {
  waiting_customer: 'neutral',
  customer_working: 'info',
  customer_done: 'warning',
  operator_working: 'info',
  completed: 'success',
};

export const RUNTIME_LABEL: Record<BrowserRuntime, string> = {
  closed: '닫힘',
  launching: '시작 중',
  open: '열림',
  error: '오류',
};

export const RUNTIME_TONE: Record<BrowserRuntime, 'neutral' | 'info' | 'success' | 'danger'> = {
  closed: 'neutral',
  launching: 'info',
  open: 'success',
  error: 'danger',
};

export const MODE_LABEL: Record<StreamMode, string> = {
  screencast: '스크린캐스트',
};

export const ROLE_LABEL: Record<UserRole, string> = {
  admin: '관리자',
  member: '운영자',
};

export const NOTIFICATION_TITLE: Record<string, string> = {
  customer_done: '고객 작업 완료',
  customer_joined: '고객 접속',
  control_requested: '제어 요청',
  node_offline: '노드 오프라인',
  profile_error: '브라우저 오류',
};

export const STREAM_REASON_LABEL: Record<string, string> = {
  inactive_tab: '다른 탭이 활성화되어 있습니다',
  minimized: '노드 PC에서 브라우저 창이 최소화되었습니다',
  no_viewers: '스트리밍이 일시 중지되었습니다',
  error: '스트리밍 오류가 발생했습니다',
  node_offline: '노드가 오프라인입니다',
};

/** Audit event types -> Korean. Unknown types fall back to the raw type string. */
const AUDIT_LABEL: Record<string, string> = {
  'profile.created': '프로필 생성',
  'profile.updated': '프로필 수정',
  'profile.archived': '프로필 보관',
  'profile.data_deleted': '브라우저 데이터 삭제',
  'browser.opened': '브라우저 열림',
  'browser.open_failed': '브라우저 열기 실패',
  'browser.closed': '브라우저 닫힘',
  'browser.runtime.open': '브라우저 실행됨',
  'browser.runtime.closed': '브라우저 종료됨',
  'browser.runtime.launching': '브라우저 시작 중',
  'browser.runtime.error': '브라우저 오류',
  'support_session.created': '지원 세션 생성',
  'support_session.status': '지원 세션 상태 변경',
  'support_session.revoked': '지원 세션 폐기',
  'customer.joined_via_support_session': '고객 지원 세션 접속',
  'customer.done': '고객 완료 처리',
  'participant.joined': '참여',
  'participant.left': '퇴장',
  'control.changed': '제어권 변경',
  'tab.opened': '탭 열림',
  'tab.closed': '탭 닫힘',
  'tab.created': '새 탭',
  'tab.close_requested': '탭 닫기 요청',
  'tab.navigate': '주소 이동',
  'tab.navigated': '페이지 이동',
  'tab.navigation_blocked': '이동 차단',
  'window.resized': '창 크기 변경',
  'file.uploaded': '파일 업로드',
  'node.online': '노드 접속',
  'node.offline': '노드 접속 해제',
  'node.registered': '노드 등록',
  'node.removed': '노드 삭제',
  'node.token_rotated': '노드 토큰 재발급',
  'auth.login': '로그인',
  'user.invited': '운영자 초대',
  'user.joined': '운영자 가입',
  'user.updated': '사용자 수정',
};

export function auditLabel(type: string): string {
  return AUDIT_LABEL[type] ?? type;
}

export const ACTOR_LABEL: Record<string, string> = {
  user: '운영자',
  customer: '고객',
  node: '노드',
  system: '시스템',
};

/** Server error codes -> Korean messages. Falls back to the server message. */
const ERROR_TEXT: Record<string, string> = {
  unauthorized: '로그인이 필요합니다',
  forbidden: '권한이 없습니다',
  not_found: '대상을 찾을 수 없습니다',
  node_offline: '노드가 오프라인입니다',
  node_timeout: '노드가 응답하지 않습니다',
  profile_archived: '보관된 프로필입니다',
  browser_not_open: '브라우저가 열려 있지 않습니다',
  bad_url: 'http(s) 주소만 사용할 수 있습니다',
  bad_proxy: '프록시 주소 형식이 올바르지 않습니다 (예: http://host:port)',
  support_session_expired: '만료된 지원 세션입니다',
  support_session_exhausted: '접속 횟수를 초과한 지원 세션입니다',
  support_session_completed: '이미 완료된 지원 세션입니다',
  email_taken: '이미 등록된 이메일입니다',
  email_mismatch: '초대된 이메일과 다릅니다',
  self_edit: '자기 자신의 역할이나 상태는 바꿀 수 없습니다',
  too_large: '업로드 용량 제한을 초과했습니다',
  single_file: '이 입력은 파일 하나만 받습니다',
  not_controller: '제어권이 없습니다',
  already_joined: '이미 참여 중입니다',
  protocol: '클라이언트 버전이 서버와 맞지 않습니다. 새로고침해주세요',
  internal: '서버 오류가 발생했습니다',
  http_error: '요청에 실패했습니다',
  network: '서버에 연결할 수 없습니다',
};

export function errorText(code: string, fallback?: string): string {
  const known = ERROR_TEXT[code];
  if (known) return known;
  if (code === 'validation' && fallback) return `입력값을 확인해주세요: ${fallback}`;
  return fallback || '알 수 없는 오류가 발생했습니다';
}
